import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import TaxDragComboChart from '../concepts/TaxDragComboChart';
import TaxDragHcChart from '../concepts/TaxDragHcChart';
import TaxDragRmdChart from '../concepts/TaxDragRmdChart';
import MtrGraph from '../concepts/MtrGraph';

const CorePrinciplesTab = ({ inputInfo, userInfo, recommendedConversion, retireYearData, scrollToChart, clearScrollToChart }) => {
  const [selectedPrinciple, _setSelectedPrinciple] = useState(() => localStorage.getItem('selectedPrinciple') || null);
  const setSelectedPrinciple = (val) => {
    _setSelectedPrinciple(val);
    if (val) localStorage.setItem('selectedPrinciple', val);
    else localStorage.removeItem('selectedPrinciple');
  };

  // Shared state for all tax-drag charts
  const defaultConvAmt = recommendedConversion?.conv_amt ? Math.round(recommendedConversion.conv_amt) : 100000;
  const defaultConvTax = (inputInfo?.tax_payment_source === 'I') ? 0 : (recommendedConversion?.conv_tax ? Math.round(recommendedConversion.conv_tax) : 20000);
  const defaultReturn = inputInfo?.dist_return_assum ? Math.round(inputInfo.dist_return_assum * 1000) / 10 : 7;
  const defaultDrag = inputInfo?.tax_drag_assum ? Math.round(inputInfo.tax_drag_assum * 1000) / 10 : 5;
  const defaultCurrentAge = (() => {
    if (!userInfo?.birth_date) return 68;
    return new Date().getFullYear() - new Date(userInfo.birth_date).getFullYear();
  })();
  const defaultLifeYears = inputInfo?.life_years || 20;
  const defaultMethod = inputInfo?.distribution_method || 'F';
  const defaultReinvestPct = inputInfo?.dist_reinvest_pct != null ? inputInfo.dist_reinvest_pct : (defaultMethod === 'R' ? 0.75 : 0.2);
  // 2026-08-23: tD for the RMD-reduction chart — the rate on displaced Traditional distributions
  const distTaxRate = recommendedConversion?.conv_dist_tax_rate_tw || 0;
  const defaultYearsToStart = (() => {
    if (!userInfo?.birth_date) return 3;
    const currentAge = new Date().getFullYear() - new Date(userInfo.birth_date).getFullYear();
    const distStartAge = inputInfo?.dist_start_age || 62;
    return Math.max(0, distStartAge - currentAge - 1);
  })();

  const [convAmt, setConvAmt] = useState(defaultConvAmt);
  const [convTax, setConvTax] = useState(defaultConvTax);
  const [returnRate, setReturnRate] = useState(defaultReturn);
  const [dragRate, setDragRate] = useState(defaultDrag);
  const [currentAge, setCurrentAge] = useState(defaultCurrentAge);
  const [lifeYears, setLifeYears] = useState(defaultLifeYears);
  const [distMethod, setDistMethod] = useState(defaultMethod);
  const [reinvestPct, setReinvestPct] = useState(defaultReinvestPct);

  // Update state when props load after initial render
  useEffect(() => {
    if (recommendedConversion?.conv_amt) setConvAmt(Math.round(recommendedConversion.conv_amt));
    if (recommendedConversion?.conv_tax) setConvTax(inputInfo?.tax_payment_source === 'I' ? 0 : Math.round(recommendedConversion.conv_tax));
  }, [recommendedConversion, inputInfo?.tax_payment_source]);

  useEffect(() => {
    if (inputInfo?.dist_return_assum) setReturnRate(Math.round(inputInfo.dist_return_assum * 1000) / 10);
    if (inputInfo?.tax_drag_assum) setDragRate(Math.round(inputInfo.tax_drag_assum * 1000) / 10);
    if (inputInfo?.life_years) setLifeYears(inputInfo.life_years);
    if (inputInfo?.distribution_method) setDistMethod(inputInfo.distribution_method);
    if (inputInfo?.dist_reinvest_pct != null) setReinvestPct(inputInfo.dist_reinvest_pct);
    else if (inputInfo?.distribution_method) setReinvestPct(inputInfo.distribution_method === 'R' ? 1.0 : 0.2);
  }, [inputInfo]);

  useEffect(() => {
    if (userInfo?.birth_date) setCurrentAge(new Date().getFullYear() - new Date(userInfo.birth_date).getFullYear());
  }, [userInfo]);

  useEffect(() => {
    if (scrollToChart) {
      setSelectedPrinciple('tax-drag');
      setTimeout(() => {
        document.getElementById(scrollToChart)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        clearScrollToChart?.();
      }, 400);
    }
  }, [scrollToChart, clearScrollToChart]);

  const taxDragCharts = (
    <Box sx={{ mt: 1 }}>
      <div id="cp-chart-combo"><TaxDragComboChart
        convAmt={convAmt} setConvAmt={setConvAmt} convTax={convTax} setConvTax={setConvTax}
        returnRate={returnRate} setReturnRate={setReturnRate} dragRate={dragRate} setDragRate={setDragRate}
        currentAge={currentAge} setCurrentAge={setCurrentAge} lifeYears={lifeYears} setLifeYears={setLifeYears}
        distMethod={distMethod} setDistMethod={setDistMethod} yearsToStart={defaultYearsToStart}
        reinvestPct={reinvestPct} distTaxRate={distTaxRate}
      /></div>
      <Box sx={{ mt: 1 }} />
      <div id="cp-chart-rmd-drag"><TaxDragRmdChart
        convAmt={convAmt} setConvAmt={setConvAmt} convTax={convTax}
        returnRate={returnRate} setReturnRate={setReturnRate} dragRate={dragRate} setDragRate={setDragRate}
        currentAge={currentAge} setCurrentAge={setCurrentAge} lifeYears={lifeYears} setLifeYears={setLifeYears}
        distMethod={distMethod} setDistMethod={setDistMethod} yearsToStart={defaultYearsToStart}
        reinvestPct={reinvestPct} distTaxRate={distTaxRate}
      /></div>
      <Box sx={{ mt: 1 }} />
      <div id="cp-chart-hc-drag"><TaxDragHcChart
        convTax={convTax} setConvTax={setConvTax}
        returnRate={returnRate} setReturnRate={setReturnRate} dragRate={dragRate} setDragRate={setDragRate}
        lifeYears={lifeYears} setLifeYears={setLifeYears}
        distMethod={distMethod} setDistMethod={setDistMethod} yearsToStart={defaultYearsToStart}
        currentAge={currentAge}
      /></div>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ pt: 0, pb: 4 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Paper
          elevation={selectedPrinciple === 'tax-drag' ? 8 : 2}
          onClick={() => setSelectedPrinciple(selectedPrinciple === 'tax-drag' ? null : 'tax-drag')}
          sx={{
            flex: '1 1 300px',
            maxWidth: 420,
            p: 1.5,
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            borderLeft: '4px solid #1565c0',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 0 }}>
            Tax Drag Avoidance Benefits Quantified
          </Typography>
          <Typography variant="body2" sx={{ color: '#333' }}>
            1) ${convAmt.toLocaleString()} Conversion reduces future RMDs
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mb: 1 }}>
            2) ${convTax.toLocaleString()} Outside tax payment moves from taxable account into tax-free Roth
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, display: 'block', lineHeight: 1.3 }}>
            <strong style={{ color: '#000' }}>Charts</strong> illustrate how many more dollars you earn by moving money into or keeping money inside the Roth
          </Typography>
        </Paper>

        <Paper
          elevation={selectedPrinciple === 'ss-mtr' ? 8 : 2}
          onClick={() => setSelectedPrinciple(selectedPrinciple === 'ss-mtr' ? null : 'ss-mtr')}
          sx={{
            flex: '1 1 300px',
            maxWidth: 420,
            p: 1.5,
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            borderLeft: '4px solid #e65100',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0 }}>
            Social Security & Marginal Tax Rates
          </Typography>
          <Typography variant="caption" color="text.secondary">
            See how Social Security taxation creates hidden marginal rate spikes
          </Typography>
        </Paper>
      </Box>

      {selectedPrinciple === 'tax-drag' && taxDragCharts}

      {selectedPrinciple === 'ss-mtr' && (
        <Box sx={{ mt: 1 }}>
          <div id="cp-chart-mtr">
            <MtrGraph inputInfo={inputInfo} userInfo={userInfo} retireYearData={retireYearData} />
          </div>

          <Paper
            elevation={1}
            sx={{
              mt: 2,
              pt: 0.5,
              pb: 1,
              px: 2.5,
              backgroundColor: '#e3f2fd',
              border: '2px solid #b3e5fc',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 0.5, textAlign: 'center', letterSpacing: 0.5 }}>
              SOCIAL SECURITY BENEFIT – HOW IT'S TAXED
            </Typography>

            <Typography variant="body2" sx={{ mb: 1, color: '#1a237e' }}>
              <strong style={{ color: '#0d47a1' }}>Taxable Portion of Social Security Benefit</strong> — Calculation is based on Provisional Income (PI) where:
            </Typography>

            <Box sx={{ backgroundColor: '#bbdefb', borderRadius: 1, p: 1.25, mb: 1.5, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#0d47a1' }}>
                PI = ½ SS Benefit + Other Taxable Income + Tax-Free Interest
              </Typography>
            </Box>

            <Box component="ul" sx={{ pl: 3, m: 0, mb: 1.5, color: '#1a237e' }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                <strong>50%</strong> of PI over <strong>$25,000</strong> up to <strong>$34,000</strong> for individuals (<strong>$32,000 &amp; $44,000</strong> for MFJ) is taxable
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                <strong>85%</strong> of PI over <strong>$34,000</strong> for individuals (<strong>$44,000</strong> for MFJ) is taxable
              </Typography>
              <Typography component="li" variant="body2">
                The taxable portion of the SS benefit <strong>cannot exceed 85%</strong>
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontStyle: 'italic', lineHeight: 1.3 }}>
              <strong>Other Income</strong> = Income other than Social Security; here we ignore tax-free interest.<br />
              Social Security benefits aren't generally taxable without 'other' taxable income, which combined determine the taxable portion.
            </Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default CorePrinciplesTab;
